import { z } from 'zod';
import { niNumberSchema, nonEmptyTrimmed } from './primitives.js';

const utr10 = z
  .string({ required_error: 'Enter your 10-digit UTR' })
  .trim()
  .regex(/^\d{10}$/, 'UTR must be exactly 10 digits');

// What the system sends to HMRC verification. Everything here is pulled from the
// application — a sole trader needs name + UTR; a limited company needs its name + UTR.
export const cisVerificationInputSchema = z.discriminatedUnion('entityType', [
  z.object({
    entityType: z.literal('soleTrader'),
    utr: utr10,
    firstName: nonEmptyTrimmed('First name', 60),
    lastName: nonEmptyTrimmed('Surname', 60),
    // Optional but recommended by HMRC — sending the NI number alongside the UTR improves
    // the chance of a successful match.
    niNumber: niNumberSchema.optional(),
  }),
  z.object({
    entityType: z.literal('company'),
    utr: utr10,
    companyName: nonEmptyTrimmed('Company name', 160),
  }),
]);

export type CisVerificationInput = z.infer<typeof cisVerificationInputSchema>;

// gross = 0%, net = 20% standard (the ~99% case), higher = 30% (unverified / not matched).
export const cisTaxTreatmentSchema = z.enum(['gross', 'net', 'higher']);
export const cisMatchStatusSchema = z.enum(['matched', 'unmatched']);
export type CisTaxTreatment = z.infer<typeof cisTaxTreatmentSchema>;
export type CisMatchStatus = z.infer<typeof cisMatchStatusSchema>;

export const cisVerificationResultSchema = z.object({
  verificationNumber: z.string(),
  matchStatus: cisMatchStatusSchema,
  taxTreatment: cisTaxTreatmentSchema,
  taxRate: z.union([z.literal(0), z.literal(20), z.literal(30)]),
  verifiedAt: z.string(),
  // 'stub' marks a result that did not come from the real HMRC bot — useful for admins
  // while credentials/captures are pending.
  mode: z.enum(['stub', 'live']),
});

export type CisVerificationResult = z.infer<typeof cisVerificationResultSchema>;
